import { hexToPixel } from '../../game/world-map/terrial-engine/HexUtils'
import { TILE_RENDER_HEIGHT, TILE_RENDER_WIDTH } from '../../game/world-map/terrial-engine/types'
import {
  ESPIONAGE_V2_MAP_TILE_MAP_HEIGHT,
  ESPIONAGE_V2_WORLD_MAP_BACKGROUND,
} from './espionagePageV2Layout'
import type {
  EspionageV2MapCamera,
  EspionageV2MapTransform,
  EspionageV2MapViewport,
} from './espionageV2WorldMapUtils'
import {
  espionageMapToScreenPoint,
  getEspionageVisibleMapBounds,
  worldToEspionageMapPoint,
} from './espionageV2WorldMapUtils'

let mapImage: HTMLImageElement | null = null
let loadListeners: Array<() => void> = []

/** World pixel rect that matches the main world map (hex grid extents). */
export function computeEspionageWorldPixelBounds(
  worldWidth: number,
  worldHeight: number,
): { minX: number; minY: number; maxX: number; maxY: number } {
  const topLeft = hexToPixel(0, 0)
  const bottomRight = hexToPixel(worldWidth - 1, worldHeight - 1)
  return {
    minX: topLeft.x - TILE_RENDER_WIDTH / 2,
    minY: topLeft.y - TILE_RENDER_HEIGHT / 2,
    maxX: bottomRight.x + TILE_RENDER_WIDTH / 2,
    maxY: bottomRight.y + TILE_RENDER_HEIGHT / 2,
  }
}

export function preloadEspionageV2WorldMapBackground(): void {
  if (mapImage?.complete && mapImage.naturalWidth > 0) return
  if (mapImage) return

  const img = new Image()
  img.decoding = 'async'
  img.onload = () => {
    mapImage = img
    const pending = loadListeners
    loadListeners = []
    for (const fn of pending) fn()
  }
  img.onerror = () => {
    loadListeners = []
  }
  img.src = ESPIONAGE_V2_WORLD_MAP_BACKGROUND
}

export function subscribeEspionageV2WorldMapBackgroundLoad(listener: () => void): () => void {
  if (mapImage?.complete && mapImage.naturalWidth > 0) {
    listener()
    return () => {}
  }
  loadListeners.push(listener)
  return () => {
    loadListeners = loadListeners.filter((fn) => fn !== listener)
  }
}

/** Tile map.webp in a simple repeat grid (no flip or rotation). */
export function drawEspionageV2WorldMapBackground(
  ctx: CanvasRenderingContext2D,
  viewport: EspionageV2MapViewport,
  camera: EspionageV2MapCamera,
  transform: EspionageV2MapTransform,
  worldWidth: number,
  worldHeight: number,
): void {
  const img = mapImage
  if (!img?.complete || img.naturalWidth <= 0 || worldWidth <= 0 || worldHeight <= 0) {
    return
  }

  const bounds = computeEspionageWorldPixelBounds(worldWidth, worldHeight)
  const origin = worldToEspionageMapPoint(bounds.minX, bounds.minY, transform)

  const tileMapH = ESPIONAGE_V2_MAP_TILE_MAP_HEIGHT
  const tileMapW = (img.naturalWidth / img.naturalHeight) * tileMapH
  if (tileMapW <= 0 || tileMapH <= 0) return

  const visible = getEspionageVisibleMapBounds(viewport, camera, tileMapW)

  const startCol = Math.floor((visible.minX - origin.x) / tileMapW)
  const endCol = Math.floor((visible.maxX - origin.x) / tileMapW)
  const startRow = Math.floor((visible.minY - origin.y) / tileMapH)
  const endRow = Math.floor((visible.maxY - origin.y) / tileMapH)

  const { width: vpW, height: vpH } = viewport
  const cullMargin = Math.max(tileMapW, tileMapH)

  ctx.save()
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'

  for (let row = startRow; row <= endRow; row++) {
    for (let col = startCol; col <= endCol; col++) {
      const mapX = origin.x + col * tileMapW
      const mapY = origin.y + row * tileMapH
      const mapX2 = mapX + tileMapW
      const mapY2 = mapY + tileMapH

      const screenTL = espionageMapToScreenPoint(mapX, mapY, viewport, camera)
      const screenBR = espionageMapToScreenPoint(mapX2, mapY2, viewport, camera)

      const dw = screenBR.x - screenTL.x
      const dh = screenBR.y - screenTL.y
      if (dw <= 0 || dh <= 0) continue

      if (
        screenBR.x < -cullMargin ||
        screenTL.x > vpW + cullMargin ||
        screenBR.y < -cullMargin ||
        screenTL.y > vpH + cullMargin
      ) {
        continue
      }

      ctx.drawImage(
        img,
        0,
        0,
        img.naturalWidth,
        img.naturalHeight,
        screenTL.x,
        screenTL.y,
        dw,
        dh,
      )
    }
  }

  ctx.restore()
}
